import React, { useState, useCallback, useEffect, useMemo } from 'react';
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  MarkerType,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import {
  ArrowsOut,
  Robot,
  Sparkle,
  WarningCircle
} from '@phosphor-icons/react';

/**
 * OrgRole Data Model (source of truth)
 * This is what we store in MongoDB - never mix React Flow objects here
 */
// {
//   id: string;
//   name: string;
//   title: string;
//   department: string;
//   description?: string;
//   supervisor_role_id?: string | null;
//   ai_overlay: {
//     opportunities_count: number;
//     high_priority_opportunities_count: number;
//     linked_agents_count: number;
//     max_priority_level: string;
//   }
// }

/**
 * Custom node component for org chart roles
 * Only receives serializable data - NO functions!
 */
const RoleNode = ({ data }) => {
  const hasAgent = data.hasAgent;
  const hasHighPriorityOpportunity = data.hasHighPriorityOpportunity;
  const hasOpportunity = data.hasOpportunity;
  const opportunitiesCount = data.opportunitiesCount || 0;
  
  return (
    <div 
      className={`relative px-4 py-3 rounded-xl border-2 shadow-lg min-w-[200px] max-w-[250px] transition-all cursor-pointer hover:shadow-xl ${
        hasAgent 
          ? 'bg-gradient-to-br from-[#10B981]/20 to-[#059669]/20 border-[#10B981] hover:border-[#059669]' 
          : hasOpportunity
          ? 'bg-gradient-to-br from-[#F59E0B]/20 to-[#D97706]/20 border-[#F59E0B] hover:border-[#D97706]'
          : 'bg-[rgba(255,255,255,0.05)] border-[rgba(255,255,255,0.2)] hover:border-[#6366F1]/50'
      }`}
    >
      {/* Connection handles - invisible but required for React Flow */}
      <Handle 
        type="target" 
        position={Position.Top} 
        style={{ opacity: 0 }}
      />
      <Handle 
        type="source" 
        position={Position.Bottom} 
        style={{ opacity: 0 }}
      />
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold text-white mb-1 truncate" title={data.title}>
            {data.title || 'Untitled Role'}
          </div>
          {data.name && (
            <div className="text-xs text-[#E2E8F0] mb-1 truncate" title={data.name}>
              {data.name}
            </div>
          )}
          {data.department && (
            <div className="text-xs text-[#A0AEC0] truncate" title={data.department}>
              {data.department}
            </div>
          )}
        </div>
        
        {/* Badges */}
        <div className="flex flex-col gap-1">
          {hasAgent && (
            <div 
              className="w-6 h-6 rounded-full bg-[#10B981] flex items-center justify-center shadow-lg" 
              title="Has AI Agent"
            >
              <Robot size={14} className="text-white" />
            </div>
          )}
          {hasHighPriorityOpportunity && !hasAgent && (
            <div 
              className="w-6 h-6 rounded-full bg-[#F59E0B] flex items-center justify-center shadow-lg" 
              title="High Priority AI Opportunity"
            >
              <Sparkle size={14} className="text-white" />
            </div>
          )}
          {opportunitiesCount > 0 && (
            <div 
              className="px-2 py-0.5 rounded-full bg-[#6366F1] text-white text-[10px] font-semibold text-center" 
              title={`${opportunitiesCount} AI Opportunities`}
            >
              {opportunitiesCount}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const nodeTypes = {
  roleNode: RoleNode,
};

/**
 * Auto-layout using dagre for hierarchical structure
 */
const getLayoutedElements = (nodes, edges) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ 
    rankdir: 'TB', // Top to bottom
    nodesep: 120,  // Horizontal spacing
    ranksep: 100,  // Vertical spacing
    marginx: 50,
    marginy: 50
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 220, height: 100 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 110, // Center the node
        y: nodeWithPosition.y - 50,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

/**
 * Flatten tree structure to list of roles
 * CRITICAL: Preserves or reconstructs supervisor_role_id relationships
 */
const flattenOrgTree = (node, roles = [], parentId = null) => {
  if (!node) return roles;
  
  // Add current node if it's not the virtual root
  if (node.id !== 'virtual_root') {
    // Create role object with supervisor_role_id explicitly set
    const role = {
      ...node,
      // Use existing supervisor_role_id if present, otherwise use parentId
      supervisor_role_id: node.supervisor_role_id || parentId
    };
    
    roles.push(role);
    
    // Recursively add children with THIS node as their parent
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => flattenOrgTree(child, roles, node.id));
    }
  } else {
    // For virtual root, just process children without setting parent
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(child => flattenOrgTree(child, roles, null));
    }
  }
  
  return roles;
};

/**
 * Main OrgChartVisual Component
 * 
 * Props:
 * - orgChartData: Tree structure from API (OrgRole tree)
 * - onNodeClick: Callback when a node is clicked (roleId, roleData)
 * - onNodeDrop: Callback when a node is dropped on another (draggedRoleId, targetRoleId)
 * - editable: Boolean - whether editing is allowed (tier-based)
 */
const OrgChartVisual = ({ 
  orgChartData, 
  onNodeClick, 
  onNodeDrop, 
  editable = false,
  selectedNodeId = null,
  onCreateAgent = null,
  opportunities = [],
  linkedAgents = []
}) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [draggedNode, setDraggedNode] = useState(null);

  /**
   * Convert OrgRole tree to React Flow nodes and edges
   * This is the mapping layer - keeps React Flow separate from our data model
   */
  useEffect(() => {
    if (!orgChartData || !orgChartData.root) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Flatten tree to list of roles
    const roles = flattenOrgTree(orgChartData.root);
    
    if (roles.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }

    // Map OrgRole[] to React Flow nodes - ONLY serializable data!
    const newNodes = roles.map(role => {
      const aiOverlay = role.ai_overlay || {};
      const linkedAgentsCount = Number(aiOverlay.linked_agents_count) || 0;
      const highPriorityCount = Number(aiOverlay.high_priority_opportunities_count) || 0;
      const opportunitiesCount = Number(aiOverlay.opportunities_count) || 0;
      
      // DEBUG: Log ai_overlay for first few roles
      if (roles.indexOf(role) < 3) {
        console.log('[OrgChartVisual] Role:', role.title, 'ai_overlay:', aiOverlay, 'opportunitiesCount:', opportunitiesCount);
      }
      
      // Find supervisor name
      const supervisorRole = roles.find(r => r.id === role.supervisor_role_id);
      const supervisorName = supervisorRole ? (supervisorRole.title || supervisorRole.name || 'Unknown') : null;

      // Find opportunities for this role:
      // 1. Check role_id/linked_role_id (primary owner - legacy and new)
      // 2. Check affected_role_ids array (role is affected by this opportunity)
      // 3. Check primary_owner_role_id (new canonical model)
      const roleOpportunities = opportunities.filter(opp => {
        const roleId = role.id || role.role_id;
        const roleName = role.title || role.name;
        
        // Direct role match (primary owner)
        if (opp.role_id === roleId || opp.linked_role_id === roleId || opp.primary_owner_role_id === roleId) {
          return true;
        }
        
        // Check if role is in affected_role_ids array
        if (opp.affected_role_ids && Array.isArray(opp.affected_role_ids)) {
          if (opp.affected_role_ids.includes(roleId)) return true;
        }
        
        // Fallback: check by role name/title in affected_roles string array
        if (opp.affected_roles && Array.isArray(opp.affected_roles) && roleName) {
          if (opp.affected_roles.some(r => r.toLowerCase() === roleName.toLowerCase())) return true;
        }
        
        return false;
      });
      const roleAgents = linkedAgents.filter(agent => agent.role_id === role.id);
      
      // Determine node color based on AI status
      // P0 FIX: Use ai_overlay from backend (server-side calculation) as primary source
      // Fall back to client-side filtered roleOpportunities for backwards compatibility
      const hasAgent = roleAgents.length > 0 || linkedAgentsCount > 0;
      
      // Use ai_overlay counts (server-calculated) OR client-filtered counts
      const effectiveOpportunitiesCount = opportunitiesCount > 0 ? opportunitiesCount : roleOpportunities.length;
      const effectiveHighPriorityCount = highPriorityCount > 0 ? highPriorityCount : roleOpportunities.filter(opp => (opp.priority_score || 0) > 60).length;
      
      // Has opportunity = show orange (per legend: "High Priority Opportunity" means any AI opportunity)
      const hasOpportunity = effectiveOpportunitiesCount > 0;
      // hasHighPriorityOpp kept for backwards compat but not used for color anymore
      const hasHighPriorityOpp = effectiveHighPriorityCount > 0;

      let nodeColor = '#5C6778'; // Grey - no mapping
      if (hasAgent) {
        nodeColor = '#10B981'; // Green - has agent
      } else if (hasOpportunity) {
        nodeColor = '#F59E0B'; // Orange - has opportunity (this is "High Priority Opportunity" in legend)
      }

      return {
        id: String(role.id),
        type: 'roleNode',
        data: {
          // Core role data
          id: String(role.id),
          name: String(role.name || ''),
          title: String(role.title || ''),
          label: String(role.title || role.name || 'Untitled Role'),
          department: String(role.department || ''),
          description: String(role.description || ''),
          
          // AI overlay indicators - use server-calculated counts
          hasAgent: hasAgent,
          hasHighPriorityOpportunity: hasHighPriorityOpp,
          hasOpportunity: hasOpportunity,
          opportunitiesCount: effectiveOpportunitiesCount,
          linkedAgentsCount: hasAgent ? Math.max(roleAgents.length, linkedAgentsCount) : 0,
          nodeColor: nodeColor,
          
          // Hierarchy info
          supervisorRoleId: role.supervisor_role_id || null,
          supervisorName: supervisorName,
          // Selection state for highlighting
          isSelected: String(role.id) === String(selectedNodeId),
        },
        position: { x: 0, y: 0 }, // Will be set by dagre
        draggable: editable,
        // Highlight selected node with prominent styling
        selected: String(role.id) === String(selectedNodeId)
        // NOTE: Removed inline style that was overriding RoleNode's Tailwind classes
        // Node styling is now handled entirely by RoleNode component based on data props
      };
    });

    // Debug: Log roles with supervisor relationships
    console.log('[OrgChartVisual] All roles:', roles.length);
    console.log('[OrgChartVisual] Sample role data:', roles[0]);
    console.log('[OrgChartVisual] All role IDs:', roles.map(r => r.id));
    console.log('[OrgChartVisual] Roles with supervisor_role_id field:', roles.filter(r => r.supervisor_role_id).length);
    console.log('[OrgChartVisual] Roles with supervisors (detailed):', roles.filter(r => r.supervisor_role_id).map(r => ({
      id: r.id,
      title: r.title,
      supervisor_role_id: r.supervisor_role_id,
      supervisorExists: roles.some(parent => parent.id === r.supervisor_role_id)
    })));

    // Map parent-child relationships to React Flow edges
    // Create a set of valid node IDs for validation
    const validNodeIds = new Set(roles.map(r => String(r.id)));
    
    const newEdges = roles
      .filter(role => {
        // Must have supervisor_role_id
        if (!role.supervisor_role_id) return false;
        // Skip virtual root
        if (role.supervisor_role_id === 'virtual_root') return false;
        // CRITICAL: Check if supervisor actually exists in our nodes
        const supervisorId = String(role.supervisor_role_id);
        const roleId = String(role.id);
        if (!validNodeIds.has(supervisorId)) {
          console.warn(`[OrgChartVisual] Skipping edge: supervisor "${supervisorId}" not found for role "${roleId}" (${role.title})`);
          return false;
        }
        return true;
      })
      .map(role => ({
        id: `edge-${role.supervisor_role_id}-${role.id}`,
        source: String(role.supervisor_role_id),
        target: String(role.id),
        type: 'smoothstep', // Smooth step edges for clear hierarchy
        animated: false,
        style: { 
          stroke: 'rgba(139, 92, 246, 0.8)', // Increased opacity
          strokeWidth: 3, // Increased width for visibility
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: 'rgba(139, 92, 246, 0.8)',
        },
      }));

    console.log('[OrgChartVisual] Filtered roles for edges:', roles.filter(r => r.supervisor_role_id && r.supervisor_role_id !== 'virtual_root').length);
    console.log('[OrgChartVisual] Created edges before layout:', newEdges);

    // Apply hierarchical layout
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(
      newNodes,
      newEdges
    );

    console.log('[OrgChartVisual] Created nodes:', layoutedNodes.length);
    console.log('[OrgChartVisual] Created edges after layout:', layoutedEdges.length);
    console.log('[OrgChartVisual] Edges details:', layoutedEdges);

    setNodes(layoutedNodes);
    setEdges(layoutedEdges);
  }, [orgChartData, editable, selectedNodeId, opportunities, linkedAgents]);

  /**
   * Handle node click - pass clean role data to parent
   */
  const handleNodeClick = useCallback((event, node) => {
    if (onNodeClick) {
      // Pass only the data, not React Flow internals
      onNodeClick(node.id, node.data);
    }
  }, [onNodeClick]);

  /**
   * Handle drag start
   */
  const handleNodeDragStart = useCallback((event, node) => {
    if (editable) {
      setDraggedNode(node);
    }
  }, [editable]);

  /**
   * Handle node drop for reparenting
   */
  const handleNodeDragStop = useCallback((event, node) => {
    if (!editable || !draggedNode || !onNodeDrop) {
      setDraggedNode(null);
      return;
    }

    // Check if dropped on another node (simplified - in production, use node overlap detection)
    const draggedId = draggedNode.id;
    const targetId = node.id;
    
    // Don't allow dropping on self or current parent
    if (draggedId !== targetId && draggedNode.data.supervisorRoleId !== targetId) {
      // Call parent handler to update supervisor_role_id
      onNodeDrop(draggedId, targetId);
    }
    
    setDraggedNode(null);
  }, [editable, draggedNode, onNodeDrop]);

  /**
   * Fit view to show all nodes
   */
  const handleFitView = useCallback(() => {
    // React Flow provides fitView through ref, but we'll use the control button
  }, []);

  // Show empty state if no data
  if (!orgChartData || !orgChartData.root) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <WarningCircle size={48} className="mx-auto mb-4 text-[#6366F1]" />
          <h3 className="text-xl font-semibold text-white mb-2">No Org Chart Data</h3>
          <p className="text-[#A0AEC0]">Upload an org chart or add roles to get started</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-[#161B26]">
      {/* React Flow Chart - Full Width */}
      <div className="w-full h-full">
        <ReactFlow
          id="org-chart-flow"
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          fitView
          className="bg-[#161B26]"
          minZoom={0.1}
          maxZoom={2}
          defaultEdgeOptions={{
            style: { stroke: 'rgba(139, 92, 246, 0.8)', strokeWidth: 2 },
            animated: false,
            type: 'smoothstep',
          }}
        >
          <Controls />
          <MiniMap 
            nodeColor={(node) => {
              if (node.data?.hasAgent) return '#10B981';
              if (node.data?.hasHighPriorityOpportunity) return '#F59E0B';
              return '#6366F1';
            }}
          />
          
          {/* Legend Panel - Moved to bottom */}
          <Panel position="bottom-center">
            <div className="bg-[rgba(10,11,13,0.95)] border border-[rgba(255,255,255,0.1)] rounded-lg px-6 py-3">
              <div className="flex items-center gap-6 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#10B981]"></div>
                  <span className="text-white">Has AI Agent</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
                  <span className="text-white">High Priority Opportunity</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#6366F1] opacity-50"></div>
                  <span className="text-[#A0AEC0]">No AI mapping</span>
                </div>
              </div>
            </div>
          </Panel>
        </ReactFlow>
      </div>
    </div>
  );
};

export default OrgChartVisual;
